import { useCallback } from 'react';
import { logger } from '@gov-nx/api';
import { useAuthEvents } from '@gov-nx/core/events';
import { useUserLoginInfo } from '@gov-nx/core/hooks';
import { useAuthStore, useAuthStoreUser } from '@gov-nx/store';
import { isNiaLoginType } from '@gov-nx/utils/common';
import { useAuthMeQuery } from './useAuthMeQuery';
import { useAuthNiaRegisterQuery } from './useAuthNiaRegisterQuery';

export const useIdentity = (): PoIdentityType => {
	const { fetch: userMeQuery } = useAuthMeQuery();
	const { fetch: authNiaRegisterQuery } = useAuthNiaRegisterQuery();
	const { userLogout } = useAuthEvents();
	const accessToken = useAuthStore('token');
	const { isLoggedIn, loginType } = useAuthStoreUser();

	const isDataBoxLoginType = loginType
		? loginType.toUpperCase() === 'DS'
		: false;
	const { isLowLoa, isSubstantialLoa, isHighLoa } = useUserLoginInfo();

	const fetchMe = useCallback(
		async (): Promise<void> =>
			new Promise((resolve, reject) => userMeQuery({ resolve, reject })),
		[userMeQuery]
	);

	const poLogin = useCallback(
		async (niaToken: string): Promise<void> => {
			logger('useIdentity -> poLogin', []);
			return new Promise((resolve, reject) => {
				const result = authNiaRegisterQuery(niaToken, { resolve, reject });
				logger('authNiaRegisterQuery result', []);
				return result;
			});
		},
		[authNiaRegisterQuery]
	);

	return {
		fetchMe,
		isAuthenticated: isLoggedIn,
		accessToken,
		doLogout: userLogout,
		poLogin,
		isNiaLoginType: isNiaLoginType(loginType),
		isDataBoxLoginType,
		isLowLoa,
		isSubstantialLoa,
		isHighLoa,
	};
};

export type PoIdentityType = {
	isAuthenticated: boolean;
	accessToken: string | undefined;
	fetchMe: () => Promise<void>;
	poLogin: (niaToken: string) => Promise<void>;
	doLogout: () => void;
	isNiaLoginType: boolean;
	isDataBoxLoginType: boolean;
	isLowLoa: boolean;
	isSubstantialLoa: boolean;
	isHighLoa: boolean;
};
